"use client";

import React from "react";
import { Terminal, Phone, Mail, MapPin, Gamepad2, Send, ShieldAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
function CrewContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-12 text-center border border-primary/30 bg-primary/5 rounded-md backdrop-blur-md">
        <div className="size-16 rounded-md bg-primary/20 flex items-center justify-center mb-6">
          <Terminal className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground mb-2">Inquiry Received</h3>
        <p className="text-muted-foreground mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary/50 hover:bg-primary/10 text-primary rounded-md">
          Send Another Message
        </Button>
      </div>;
  }
  const inputClasses = "h-14 border-0 border-b-2 border-border bg-transparent focus-visible:border-primary focus-visible:ring-0 rounded-md font-sans text-base transition-all duration-300";
  const labelClasses = "text-primary font-mono uppercase tracking-widest text-xs flex items-center gap-2";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>
                <Terminal className="size-3" /> Client Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className={inputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-mono text-muted-foreground uppercase">Individual or Company Name</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>
                <Mail className="size-3" /> Business Email
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@company.com" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Invalid phone format"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>
                <Phone className="size-3" /> Contact Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="0468 039 299" className={inputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-mono text-muted-foreground uppercase">Optional – for direct consultation</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="reason" rules={{
        required: "Please select a service category"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>
                <Gamepad2 className="size-3" /> Project Type
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={inputClasses}>
                    <SelectValue placeholder="Select Service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="border-border bg-card/95 backdrop-blur-xl font-sans">
                  <SelectItem value="Custom Software">Custom Software Development</SelectItem>
                  <SelectItem value="Cybersecurity">Cybersecurity</SelectItem>
                  <SelectItem value="AI & Automation">AI & Automation</SelectItem>
                  <SelectItem value="Web & Mobile">Web & Mobile App Development</SelectItem>
                  <SelectItem value="IT Consulting">IT Consulting</SelectItem>
                  <SelectItem value="Managed IT">Managed IT Services</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Project details are required",
      minLength: {
        value: 20,
        message: "Please provide more detail (min 20 chars)"
      },
      maxLength: {
        value: 1000,
        message: "Message exceeds maximum capacity"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClasses}>
              <Terminal className="size-3" /> Project Brief
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your technical requirements or business challenges..." className={`min-h-[160px] resize-none ${inputClasses}`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-md flex items-start gap-3">
          <ShieldAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-destructive font-mono text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-16 text-lg font-default font-bold tracking-widest uppercase bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-primary-foreground rounded-md shadow-lg transition-all duration-300">
        {isSubmitting ? <span className="flex items-center gap-2 animate-pulse">
            <Terminal className="size-5" /> Processing...
          </span> : <span className="flex items-center gap-2">
            <Send className="size-5" /> Send Message
          </span>}
      </Button>
    </Form>;
}
export default function StartTheConversation() {
  return <section data-section-id="237303" id="start-the-conversation" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary/5 rounded-lg blur-[120px] pointer-events-none -translate-y-1/2" />
      <div className="absolute bottom-0 right-1/4 w-[800px] h-[800px] bg-secondary/5 rounded-lg blur-[150px] pointer-events-none translate-y-1/3" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-default font-bold uppercase tracking-tight mb-4">
            Start the <span className="text-primary">Conversation</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto font-default">
            Partner with Apex IT Solutions to transform your digital infrastructure. Our experts are ready to architect your next breakthrough.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 max-w-6xl mx-auto">
          {}
          <div className="lg:col-span-8">
            <Card className="border-white/10 bg-card/40 backdrop-blur-xl shadow-2xl rounded-md overflow-hidden">
              <div className="h-1 w-full bg-gradient-to-r from-primary via-secondary to-primary" />
              <CardHeader className="border-b border-white/5 bg-background/50 pb-4">
                <CardTitle className="font-mono text-sm text-primary uppercase tracking-widest flex items-center gap-2">
                  <Terminal className="size-4" /> Project Inquiry Form
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-8">
                <FormProvider formKey="conversation_form_connect" formId="conversation_form_connect" sectionName="Start the Conversation" defaultValues={{
                name: "",
                email: "",
                phone: "",
                reason: "General Inquiry",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  pattern: "phone"
                },
                reason: {
                  required: true
                },
                message: {
                  required: true,
                  minLength: 20,
                  maxLength: 1000
                }
              }} submitText="Send Message" successMessage="Your inquiry has been successfully transmitted. An IT consultant will contact you shortly.">
                  <CrewContactForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="lg:col-span-4 space-y-6">
            <Card className="border-white/10 bg-card/40 backdrop-blur-xl rounded-md h-full">
              <CardHeader className="border-b border-white/5 bg-background/50 pb-4">
                <CardTitle className="font-mono text-sm text-primary uppercase tracking-widest flex items-center gap-2">
                  <ShieldAlert className="size-4" /> Contact Details
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-8 space-y-8">
                <div className="space-y-2">
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Headquarters</p>
                  <div className="flex items-start gap-3 text-foreground">
                    <MapPin className="size-5 text-primary shrink-0 mt-0.5" />
                    <span className="font-default leading-relaxed">
                      Apex IT Solutions AU<br />
                      23 Collins St<br />
                      Ceduna SA 5690
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Direct Line</p>
                  <div className="flex items-center gap-3 text-foreground">
                    <Phone className="size-5 text-primary shrink-0" />
                    <span className="font-mono text-lg">0468 039 299</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Digital Mail</p>
                  <div className="flex items-center gap-3 text-foreground">
                    <Mail className="size-5 text-primary shrink-0" />
                    <span className="font-mono">info@apexitsolutionsau.com</span>
                  </div>
                </div>

                <div className="pt-6 border-t border-white/5">
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-4">Professional Networks</p>
                  <Link to="/ConnectPage" className="flex items-center justify-center gap-2 w-full h-12 border border-secondary/30 bg-secondary/5 hover:bg-secondary/10 text-secondary font-mono uppercase tracking-wider text-sm transition-all rounded-md">
                    <Gamepad2 className="size-4" /> View All Solutions
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}